import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { CheckCircle, XCircle } from '@phosphor-icons/react';

const SOURCE_LABELS = { call: 'Phone Call', website: 'Website', whatsapp: 'WhatsApp' };

const getSeverityColor = (sev) => {
  const map = { critical: 'bg-red-600 text-white', high: 'bg-red-100 text-red-800', medium: 'bg-amber-100 text-amber-800', low: 'bg-green-100 text-green-800' };
  return map[sev] || 'bg-slate-100 text-slate-800';
};

const getStatusColor = (status) => {
  const map = { reported: 'bg-amber-100 text-amber-800', approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800' };
  return map[status] || 'bg-slate-100 text-slate-800';
};

export function IncidentDetail({ incident, open, onOpenChange, canReview, reviewRemarks, setReviewRemarks, onReview }) {
  if (!incident) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Outfit' }}>
            {incident.incident_number}
            <span className={`ml-3 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(incident.status)}`}>{incident.status}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <span className="text-xs text-slate-500 uppercase">Sector</span>
              <div className="text-sm font-medium capitalize">{incident.sector}</div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Source</span>
              <div className="text-sm font-medium">{SOURCE_LABELS[incident.notification_source]}</div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Event Type</span>
              <div className="text-sm font-medium capitalize">{incident.incident_type?.replace('_', ' ')}</div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Severity</span>
              <div><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(incident.severity)}`}>{incident.severity}</span></div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Date</span>
              <div className="text-sm font-medium">{incident.date_occurred} {incident.time_occurred && `at ${incident.time_occurred} UTC`}</div>
            </div>
            <div>
              <span className="text-xs text-slate-500 uppercase">Location</span>
              <div className="text-sm font-medium">{incident.location}</div>
            </div>
          </div>

          {incident.reporter_name && (
            <div className="bg-slate-50 p-3 rounded-lg">
              <span className="text-xs text-slate-500 uppercase block mb-1">Reporter</span>
              <div className="text-sm">{incident.reporter_name} {incident.reporter_phone && `| ${incident.reporter_phone}`} {incident.reporter_organization && `| ${incident.reporter_organization}`}</div>
            </div>
          )}

          {incident.sector === 'aviation' ? (
            (incident.aircraft_registration || incident.aircraft_type) && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <span className="text-xs text-blue-600 uppercase block mb-1">Aircraft Details</span>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {incident.aircraft_registration && <div><span className="text-slate-500">Registration:</span> {incident.aircraft_registration}</div>}
                  {incident.aircraft_type && <div><span className="text-slate-500">Type:</span> {incident.aircraft_type}</div>}
                  {incident.flight_number && <div><span className="text-slate-500">Flight:</span> {incident.flight_number}</div>}
                  {incident.operator_name && <div><span className="text-slate-500">Operator:</span> {incident.operator_name}</div>}
                </div>
              </div>
            )
          ) : (
            (incident.vessel_name || incident.vessel_type) && (
              <div className="bg-cyan-50 p-3 rounded-lg">
                <span className="text-xs text-cyan-600 uppercase block mb-1">Vessel Details</span>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {incident.vessel_name && <div><span className="text-slate-500">Name:</span> {incident.vessel_name}</div>}
                  {incident.vessel_type && <div><span className="text-slate-500">Type:</span> {incident.vessel_type}</div>}
                  {incident.imo_number && <div><span className="text-slate-500">IMO:</span> {incident.imo_number}</div>}
                  {incident.vessel_flag && <div><span className="text-slate-500">Flag:</span> {incident.vessel_flag}</div>}
                </div>
              </div>
            )
          )}

          <div>
            <span className="text-xs text-slate-500 uppercase block mb-1">Description</span>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{incident.description}</p>
          </div>

          {incident.investigation_id && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <span className="text-xs text-green-700 uppercase block mb-1">Investigation Created</span>
              <div className="text-sm font-medium text-green-800">Investigation ID: {incident.investigation_id}</div>
            </div>
          )}

          {canReview && incident.status === 'reported' && (
            <div className="border-t pt-4 space-y-3">
              <Label>Review Remarks</Label>
              <Textarea value={reviewRemarks} onChange={(e) => setReviewRemarks(e.target.value)} placeholder="Optional remarks for approval/rejection..." className="rounded-lg" data-testid="review-remarks" />
              <div className="flex gap-3">
                <Button className="bg-green-600 hover:bg-green-700 text-white rounded-lg" onClick={() => onReview(incident.id, 'approve')} data-testid="approve-btn">
                  <CheckCircle size={18} className="mr-2" />
                  Approve for Investigation
                </Button>
                <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 rounded-lg" onClick={() => onReview(incident.id, 'reject')} data-testid="reject-btn">
                  <XCircle size={18} className="mr-2" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {incident.reviewed_by && (
            <div className="text-xs text-slate-500 border-t pt-3">
              Reviewed by: {incident.reviewed_by} | {incident.review_remarks && `Remarks: ${incident.review_remarks}`}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
